import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { HugeiconsIcon } from "@hugeicons/react";
import { SquareLock01FreeIcons, ViewFreeIcons, ViewOffSlashFreeIcons } from "@hugeicons/core-free-icons";

import { RegistrationFormLayout } from "../FormLayout.tsx";
import { RegistrationFormHeader } from "../FormHeader.tsx";
import { StepNavigation } from "../StepNavigation.tsx";
import { PasswordStrengthIndicator } from "../PasswordStrengthIndicator.tsx";

import { useRegistrationStore } from "@/store/registrationStore.ts";
import { Button } from "@/components/Button.tsx";
import { Input } from "@/components/Input.tsx";
import { i18n } from "@/localization";
import type { Step3FormData } from "@/schemas/registrationSchemas.ts";
import { createStep3Schema } from "@/schemas/registrationSchemas.ts";
import type { TranslationKey } from "@/localization/resources/keys.ts";
import { useCreateOrUpdateUserMutation } from "@/api/user.ts";
import { findBackendErrorDetail, isClientError, handleErrorMessage } from "@/api/utils.ts";
import FormErrorMessage from "@/components/FormErrorMessage.tsx";
import { allRulesMet } from "@/utils/passwordRules.ts";

export default function RegistrationStep3() {
	const { form, reset } = useRegistrationStore();
	const [showPassword, setShowPassword] = useState(false);
	const [showRepeat, setShowRepeat] = useState(false);
	const [isRedirecting, setIsRedirecting] = useState(false);
	const [showStrength, setShowStrength] = useState(false);
	const [sessionExpired, setSessionExpired] = useState(false);
	const { mutateAsync: createOrUpdateUserAsync } = useCreateOrUpdateUserMutation();

	const {
		register,
		handleSubmit,
		watch,
		formState: { errors, isSubmitting },
		setError
	} = useForm<Step3FormData>({
		defaultValues: {
			password: "",
			confirmPassword: ""
		},
		resolver: zodResolver(createStep3Schema(form.email))
	});

	const password = watch("password");
	const confirmPassword = watch("confirmPassword");
	const canSubmit = allRulesMet(password, form.email) && confirmPassword.length > 0;

	const onSubmit = async (data: Step3FormData) => {
		try {
			const res = await createOrUpdateUserAsync({
				email: form.email,
				firstName: form.firstName,
				lastName: form.lastName,
				password: data.password,
				step1CompletedToken: form.step1CompletedToken // must be set in Step2
			});

			setIsRedirecting(true);
			window.location.href = res.data.redirectUrl;
		} catch (e) {
			setIsRedirecting(false);

			if (findBackendErrorDetail(e, "STEP1_TOKEN_EXPIRED")) {
				setSessionExpired(true);
				setError("root", { type: "manual", message: i18n("validation.sessionExpired") });

				return;
			}

			if (isClientError(e)) {
				setError("password", { message: handleErrorMessage(e) || i18n("validation.generalError") });

				return;
			}

			setError("root", { type: "manual", message: i18n("step3.registrationError") });
		}
	};

	return (
		<RegistrationFormLayout>
			<form onSubmit={handleSubmit(onSubmit)}>
				<div className="flex flex-col gap-10">
					<RegistrationFormHeader title={i18n("step3.title")} description={i18n("step3.description")} />

					<div className="flex flex-col gap-6">
						<div className="flex flex-col gap-3">
							<Input
								label={i18n("step3.password")}
								type={showPassword ? "text" : "password"}
								error={errors.password?.message && i18n(errors.password.message as TranslationKey)}
								prefixes={<HugeiconsIcon icon={SquareLock01FreeIcons} size={20} />}
								suffixes={
									<button
										type="button"
										onClick={() => setShowPassword((prev) => !prev)}
										aria-label={
											showPassword ? i18n("step3.hidePassword") : i18n("step3.showPassword")
										}
									>
										{showPassword ? (
											<HugeiconsIcon icon={ViewFreeIcons} size={20} />
										) : (
											<HugeiconsIcon icon={ViewOffSlashFreeIcons} size={20} />
										)}
									</button>
								}
								{...register("password")}
								onFocus={() => setShowStrength(true)}
							/>

							{(showStrength || password.length > 0) && (
								<PasswordStrengthIndicator password={password} email={form.email} />
							)}
						</div>

						<Input
							label={i18n("step3.confirmPassword")}
							type={showRepeat ? "text" : "password"}
							error={
								errors.confirmPassword?.message &&
								i18n(errors.confirmPassword.message as TranslationKey)
							}
							prefixes={<HugeiconsIcon icon={SquareLock01FreeIcons} size={20} />}
							suffixes={
								<button
									type="button"
									onClick={() => setShowRepeat((prev) => !prev)}
									aria-label={showRepeat ? i18n("step3.hidePassword") : i18n("step3.showPassword")}
									tabIndex={-1}
								>
									{showRepeat ? (
										<HugeiconsIcon icon={ViewFreeIcons} size={20} />
									) : (
										<HugeiconsIcon icon={ViewOffSlashFreeIcons} size={20} />
									)}
								</button>
							}
							{...register("confirmPassword")}
						/>

						{errors.root && <FormErrorMessage>{errors.root.message}</FormErrorMessage>}
					</div>

					{sessionExpired ? (
						<Button type="button" variant="primary" onClick={reset}>
							{i18n("step3.startOver")}
						</Button>
					) : (
						<Button
							type="submit"
							variant="primary"
							disabled={!canSubmit}
							loading={isSubmitting || isRedirecting}
						>
							{i18n("step3.submit")}
						</Button>
					)}

					<StepNavigation />
				</div>
			</form>
		</RegistrationFormLayout>
	);
}
